import React, { useState } from "react";
import { Mail, Building2, MapPin, Send, MessageCircle, Linkedin, Globe, Briefcase } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Panel, Overline } from "../components/common/Panel";
import { sendContact } from "../lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors";

const INFO = [
  { icon: Mail,          label: "Email",       value: "hhnk3693@gmail.com" },
  { icon: Building2,     label: "Institution", value: "Cape Peninsula University of Technology (CPUT)" },
  { icon: MapPin,        label: "Location",    value: "Cape Town, South Africa" },
];

const QUICK = [
  { icon: Globe,         label: "Portfolio",     href: "https://parkade-stitch-site.pages.dev" },
  { icon: Briefcase,     label: "Book Services", href: "https://wa.me/27754531797" },
  { icon: MessageCircle, label: "WhatsApp",      href: "https://wa.me/27754531797" },
  { icon: Linkedin,      label: "LinkedIn",      href: "https://linkedin.com" },
];

export default function ContactPublic() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill in all fields"); return; }
    setBusy(true);
    try {
      await sendContact(form);
      toast.success("Message sent! HNK will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.success("Message sent! HNK will get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <section className="w-full px-4 sm:px-8 lg:px-12 py-16 overflow-x-hidden">
        <div className="max-w-[1400px] mx-auto">

          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--accent)]">Get in Touch</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2">Contact</h1>
            <p className="text-[var(--t2)] mt-3">Questions about the project, a demo, or collaboration? Send a message.</p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-[var(--bd)] mb-10 shadow-xl">
            <img src="/assets/laptop-dash.png" alt="Campus Energy dashboard running live" className="w-full h-auto object-contain" loading="lazy" data-testid="contact-hero-image" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* LEFT - form + map */}
            <Panel className="p-6 flex flex-col gap-6">
              <div>
                <Overline>Send a Message</Overline>
                <form onSubmit={submit} className="mt-4 space-y-4" data-testid="public-contact-form">
                  <input data-testid="public-contact-name" className={inputCls} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <input data-testid="public-contact-email" type="email" className={inputCls} placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <textarea data-testid="public-contact-message" rows={5} className={inputCls} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  <button type="submit" disabled={busy} data-testid="public-contact-submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform disabled:opacity-60">
                    <Send size={15} /> {busy ? "Sending…" : "Send Message"}
                  </button>
                </form>
              </div>

              {/* Map - same panel, below the form */}
              <div className="rounded-xl overflow-hidden border border-[var(--bd)]" data-testid="contact-map">
                <div className="px-4 py-3 bg-[var(--surface2)] border-b border-[var(--bd)] flex items-center gap-2">
                  <MapPin size={14} className="text-[var(--accent)]" />
                  <span className="font-heading font-bold text-xs">CPUT Bellville Campus, Cape Town</span>
                </div>
                <iframe
                  title="CPUT Bellville Campus"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3310.308485396046!2d18.627726776019635!3d-33.86919527224764!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcc5b130f53c18f%3A0x1bf0cf6a47baf1b5!2sCape%20Peninsula%20University%20of%20Technology%20-%20Bellville%20Campus!5e0!3m2!1sen!2sza!4v1718600000000!5m2!1sen!2sza"
                  width="100%"
                  height="280"
                  style={{ border: 0, display: "block" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Panel>

            {/* RIGHT - info, links, hardware photo */}
            <div className="space-y-4">
              <Panel className="p-6">
                <Overline>Contact Info</Overline>
                <div className="mt-4 space-y-3">
                  {INFO.map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-md bg-[var(--accent-soft)] text-[var(--accent)] flex items-center justify-center shrink-0"><Icon size={16} /></div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--t2)]">{label}</div>
                        <div className="text-sm font-mono break-words">{value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>

              <Panel className="p-6">
                <Overline>Quick Links</Overline>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {QUICK.map(({ icon: Icon, label, href }) => (
                    <a key={label} href={href} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-sm hover:border-[var(--accent)] transition-colors">
                      <Icon size={15} className="text-[var(--accent)]" /> {label}
                    </a>
                  ))}
                </div>
              </Panel>

              {/* Hardware photo - full image, no crop */}
              <Panel className="overflow-hidden" data-testid="contact-device-image">
                <img
                  src="/assets/cover.jpg"
                  alt="Campus Energy Monitor hardware built in Cape Town"
                  className="w-full h-auto"
                  style={{ display: "block" }}
                  loading="lazy"
                />
                <div className="p-4">
                  <div className="font-heading font-bold text-sm">The hardware, built in Cape Town</div>
                  <p className="text-xs text-[var(--t2)] mt-1 font-mono">ESP32 · 3× PZEM-004T · DS18B20 · MQ-135 · TTGO T-Camera · 20×4 LCD</p>
                </div>
              </Panel>
            </div>

          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
